import { routes } from '@frontend/router/routes';
import { QueryKeys } from '@frontend/features/common/types';
import { voucherQueryKey } from '../voucherAction';

interface BookingUpdatedPayload {
  bookingId: string;
  state: string;
  providerStatus?: string;
  ticketsReady: boolean;
  notifyAgent?: boolean;
}

interface HoldExpiringPayload {
  bookingId: string;
  expiresAt?: string;
  notifyAgent?: boolean;
}

interface TravelServicesUpdateHandlersInput {
  queryClient: {
    invalidateQueries: (options: { queryKey: unknown[] }) => unknown;
  };
  addToast: (toast: { message: string; color: 'info' | 'warning'; action?: { label: string; onClick: () => void } }) => void;
  navigate: (path: string) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const parse = <T>(raw: unknown): T | null => {
  try {
    return typeof raw === 'string' ? (JSON.parse(raw) as T) : (raw as T);
  } catch {
    return null;
  }
};

export const createTravelServicesUpdateHandlers = ({
  queryClient,
  addToast,
  navigate,
  t,
}: TravelServicesUpdateHandlersInput) => {
  const invalidateBookingAuthority = () => {
    queryClient.invalidateQueries({ queryKey: [QueryKeys.getTravelBookings] });
  };

  const onUpdated = (raw: unknown) => {
    const payload = parse<BookingUpdatedPayload>(raw);
    if (typeof payload?.bookingId !== 'string') return;
    invalidateBookingAuthority();
    // El voucher tiene clave propia: solo el de la reserva que cambió.
    queryClient.invalidateQueries({ queryKey: voucherQueryKey(payload.bookingId) });
  };

  const action = (bookingId: string) => ({
    label: t('travelServices.updates.viewBooking'),
    onClick: () => navigate(`${routes.dashboard.travelBookings}?bookingId=${encodeURIComponent(bookingId)}`),
  });

  const onNotice = (raw: unknown) => {
    const payload = parse<BookingUpdatedPayload>(raw);
    if (typeof payload?.bookingId !== 'string' || !payload.bookingId || payload.notifyAgent !== true) return;
    addToast({
      message: payload.ticketsReady
        ? t('travelServices.updates.ticketsReady', { booking: payload.bookingId.slice(0, 8) })
        : t('travelServices.updates.stateChanged', {
          booking: payload.bookingId.slice(0, 8),
          state: t(`travelServices.bookingStates.${payload.state}`),
        }),
      color: 'info',
      action: action(payload.bookingId),
    });
  };

  const onExpiring = (raw: unknown) => {
    const payload = parse<HoldExpiringPayload>(raw);
    if (typeof payload?.bookingId !== 'string' || !payload.bookingId || payload.notifyAgent !== true) return;
    invalidateBookingAuthority();
    addToast({
      message: t('travelServices.updates.holdExpiring', {
        booking: payload.bookingId.slice(0, 8),
        date: payload.expiresAt
          ? new Date(payload.expiresAt).toLocaleString()
          : '',
      }),
      color: 'warning',
      action: action(payload.bookingId),
    });
  };

  return { onUpdated, onNotice, onExpiring };
};
